import { useTranslation } from "react-i18next";
import {
  usePrescriptionFlowSelector,
  usePrescriptionFlowSend,
} from "../context";
import type {
  PrescriptionRequestReason,
  PrescriptionUrgency,
} from "../machine";
import { prescriptionFlowSelectors } from "../selectors";
import { KioskStep } from "../../../../../components/kiosk-step";
import { useAudio } from "../../../../../lib/use-audio";
import { ManualIdentificationNotice } from "./ManualIdentificationNotice";
import { PrescriptionReasonSection } from "./prescription-reason-section";
import { PrescriptionValidationNotice, usePrescriptionRequestSubmit } from "./prescription-request-submit";
import { PrescriptionRequestSection } from "./prescription-request-section";
import { PrescriptionUrgencySection } from "./prescription-urgency-section";

/**
 * History-free request composition for manually identified existing patients.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <ManualPrescriptionRequestStep />
 * ```
 */
export function ManualPrescriptionRequestStep() {
  const { t } = useTranslation();
  useAudio("prescription-medicationSelection");
  const send = usePrescriptionFlowSend();
  const requestText = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.requestText,
  );
  const requestReason = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.requestReason,
  );
  const requestUrgency = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.requestUrgency,
  );
  const hasRequestText = Boolean(requestText?.trim());
  const { validationError, clearValidation } = usePrescriptionRequestSubmit({
    requestReason,
    hasRequestText,
    onSubmit: () => send({ type: "SUBMIT_REQUEST" }),
  });

  const selectReason = (reason: PrescriptionRequestReason) => {
    send({ type: "SET_REASON", reason });
    clearValidation();
  };
  const selectUrgency = (urgency: PrescriptionUrgency) => {
    send({ type: "SET_URGENCY", urgency });
  };
  const changeRequestText = (text: string) => {
    send({ type: "SET_REQUEST_TEXT", text });
    clearValidation();
  };

  return (
    <KioskStep
      width="default"
      title={t("self-service.prescription.medication-selection.title")}
      lead={t("self-service.prescription.medication-selection.lead")}
    >
      <ManualIdentificationNotice />
      <PrescriptionValidationNotice message={validationError} />
      <PrescriptionReasonSection
        requestReason={requestReason}
        onSelectReason={selectReason}
      />
      <PrescriptionRequestSection
        requestText={requestText}
        onRequestTextChange={changeRequestText}
      />
      <PrescriptionUrgencySection
        requestUrgency={requestUrgency}
        onSelectUrgency={selectUrgency}
      />
    </KioskStep>
  );
}
